import { AxiosError } from "axios";
import { CountryCount } from "../models/CountryCount";
import { FreelancerProfile } from "../models/FreelancerProfile";
import { PaginatedResponse } from "../models/Pagination";
import { FreelancerProfileSearchRequest } from "../models/requests/FreelancerProfileSearchRequest";
import { UpdateFreelancerProfileProfessionalExperienceRequest } from "../models/requests/UpdateFreelancerProfileProfessionalExperienceRequest";
import JSONPatchOperation from "../utils/JSONPatchOperation";
import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import { SaveProfessionalExperienceResponse } from "../models/responses/SaveProfessionalExperienceResponse";

export const getFreelancerProfile = async (
	freelancerProfileId: number,
	freelancerProfileKey: string = ""
): Promise<FreelancerProfile | null> => {
	try {
		if (freelancerProfileId <= 0 && freelancerProfileKey === "") {
			return null;
		}

		const response = await apiClient.get(`/freelancer-profiles/${freelancerProfileId}`, {
			headers: {
				"Freelancer-Profile-Key": freelancerProfileKey
			}
		});

		return response.data;
	} catch (error: any) {
		if (error.response?.status === 404) {
			return null;
		}
		throw new Error(
			error.response?.data?.message ?? "Not able to get the current freelancer profile."
		);
	}
};

/**
 * Update a freelancer using JSON Patch (PATCH)
 * @param freelancerProfile Freelancer profile
 * @param patchOperations Array of JSON Patch operations
 */
export const patchFreelancerProfile = async (
	freelancerProfile: FreelancerProfile,
	patchOperations: JSONPatchOperation[],
	languageCode?: string
): Promise<void> => {
	try {
		const url = languageCode
			? `/freelancer-profiles/${freelancerProfile.id}?languageCode=${languageCode}`
			: `/freelancer-profiles/${freelancerProfile.id}`;
		await apiClient.patch(
			url,
			patchOperations,
			{
				headers: {
					"Content-Type": "application/json-patch+json"
				}
			}
		);
	} catch (error) {
		throw handleApiError(error, "Failed to update freelancer profile");
	}
};

export const saveFreelancerOnboardingProgression = async (
	freelancerProfile: FreelancerProfile,
	patchOperations: JSONPatchOperation[],
	languageCode?: string
): Promise<void> => {
	try {
		const url = languageCode
			? `/freelancer-profiles/${freelancerProfile.id}/save-onboarding-progress?languageCode=${languageCode}`
			: `/freelancer-profiles/${freelancerProfile.id}/save-onboarding-progress`;
		await apiClient.post(
			url,
			patchOperations,
			{
				headers: {
					"Content-Type": "application/json"
				}
			}
		);
	} catch (error) {
		throw handleApiError(
			error,
			((error as AxiosError).response?.data as any)?.detail ??
				"Failed to update freelancer profile"
		);
	}
};

export const saveProfessionalExperienceAsync = async (
	freelancerProfileId: number,
	updateFreelancerProfileProfessionalExperienceRequest: UpdateFreelancerProfileProfessionalExperienceRequest,
	languageCode?: string
): Promise<SaveProfessionalExperienceResponse> => {
	try {
		const url = languageCode
			? `/freelancer-profiles/${freelancerProfileId}/professional-experiences?languageCode=${languageCode}`
			: `/freelancer-profiles/${freelancerProfileId}/professional-experiences`;
		const response = await apiClient.put(
			url,
			updateFreelancerProfileProfessionalExperienceRequest,
			{
				headers: {
					"Content-Type": "application/json"
				}
			}
		);

		return response.data;
	} catch (error) {
		throw handleApiError(
			error,
			((error as AxiosError).response?.data as any)?.detail ??
				"Failed to upsert freelancer profile professional experience"
		);
	}
};

export const deleteProfessionalExperienceAsync = async (
	freelancerProfileId: number,
	professionalExperienceId: number
): Promise<void> => {
	try {
		await apiClient.delete(
			`/freelancer-profiles/${freelancerProfileId}/professional-experiences/${professionalExperienceId}`
		);
	} catch (error) {
		throw handleApiError(
			error,
			"Failed to delete freelancer profile professional experience"
		);
	}
};

/**
 * Update a freelancer profile's "Contrôlé" status (TAM only).
 * @param freelancerProfileId The ID of the freelancer profile
 * @param newIsControlled The new controlled status to set
 */
export const updateFreelancerProfileControlledStatus = async (
	freelancerProfileId: number,
	newIsControlled: boolean
): Promise<void> => {
	try {
		await apiClient.put(
			`/freelancer-profiles/${freelancerProfileId}/is-controlled/${newIsControlled}`
		);
	} catch (error) {
		throw handleApiError(error);
	}
};

export const getFreelancerCountriesAsync = async (
	onboardingStatus?: string | null
): Promise<string[]> => {
	try {
		const params = onboardingStatus ? { onboardingStatus } : {};
		const response = await apiClient.get<string[]>(`/freelancer-profiles/countries`, { params });
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to fetch freelancer countries");
	}
};

export const getFreelancerCountryCountsAsync = async (
	onboardingStatus?: string | null
): Promise<CountryCount[]> => {
	try {
		const params = onboardingStatus != null ? { onboardingStatus } : {};
		const response = await apiClient.get<CountryCount[]>(`/freelancer-profiles/countries/counts`, {
			params
		});
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to fetch freelancer country counts");
	}
};

export const searchFreelancerProfilesAsync = async (
	freelancerProfileSearchRequest: FreelancerProfileSearchRequest
): Promise<PaginatedResponse<FreelancerProfile>> => {
	try {
		const response = await apiClient.post<PaginatedResponse<FreelancerProfile>>(
			`/freelancer-profiles/search`,
			freelancerProfileSearchRequest
		);

		// The API now returns the paginated response directly in the body
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to fetch freelancers profiles");
	}
};
